import React from "react";
import * as PPIcons from "./_icons"; // eslint-disable-line no-unused-vars

// src/components/_primitives.jsx
// Shared low-level UI primitives. All read CSS tokens from src/styles/tokens.css.
//
// CLAUDE CODE: add `import * as PPIcons from "./_icons"; // eslint-disable-line no-unused-vars` at top, and replace
// the trailing `Object.assign(window, {...})` with named exports for each.


/* ---------- Button ---------- */
function Button({
  variant = "secondary", // primary | secondary | ghost | danger
  size = "md",           // sm | md | lg | xl
  icon,
  iconRight,
  children,
  className = "",
  ...rest
}) {
  const cls = [
    "pp-btn",
    `pp-btn--${variant}`,
    size !== "md" ? `pp-btn--${size}` : "",
    !children ? "pp-btn--icon" : "",
    className,
  ].filter(Boolean).join(" ");
  return (
    <button className={cls} {...rest}>
      {icon ? icon : null}
      {children}
      {iconRight ? iconRight : null}
    </button>
  );
}

/* ---------- Chip ---------- */
function Chip({ tone, dot = true, mono = false, icon, children, className = "" }) {
  const cls = [
    "pp-chip",
    tone ? `pp-chip--${tone}` : "",
    mono ? "pp-chip--mono" : "",
    className,
  ].filter(Boolean).join(" ");
  return (
    <span className={cls}>
      {dot && tone && (tone.startsWith("sev-") || tone.startsWith("status-")) && <span className="pp-dot" />}
      {icon ? icon : null}
      {children}
    </span>
  );
}

function SeverityChip({ severity, mono }) {
  const map = { low: "Low", moderate: "Moderate", critical: "Critical" };
  const tone = severity === "critical" ? "sev-crit" : severity === "moderate" ? "sev-mod" : "sev-low";
  return <Chip tone={tone} mono={mono}>{map[severity] || severity}</Chip>;
}

function StatusChip({ status }) {
  const labels = {
    new: "New",
    investigating: "Investigating",
    resolved: "Resolved",
    dismissed: "Dismissed",
    open: "Open",
  };
  const tone = status === "open" ? "status-new" : `status-${status}`;
  return <Chip tone={tone}>{labels[status] || status}</Chip>;
}

function CategoryChip({ category }) {
  return <Chip tone="category" dot={false} mono>{category}</Chip>;
}

/* ---------- Status pipeline (mini timeline of lifecycle) ---------- */
const PIPELINE = ["new", "investigating", "resolved"];
function StatusPipeline({ status = "new" }) {
  const activeIdx = PIPELINE.indexOf(status === "open" ? "new" : status);
  return (
    <span className="pp-status-pipe" data-status={status === "open" ? "new" : status}>
      {PIPELINE.map((s, i) => (
        <span key={s} style={{ display: "inline-flex", alignItems: "center" }}>
          <span className="pp-status-pipe-node" data-active={i <= activeIdx} />
          {i < PIPELINE.length - 1 && (
            <span className="pp-status-pipe-bar" data-active={i < activeIdx} />
          )}
        </span>
      ))}
    </span>
  );
}

/* ---------- Avatar ---------- */
function Avatar({ name, size = "md", brand = false }) {
  const initials = (name || "?").trim().split(/\s+/).map(w => w[0]).join("").slice(0, 2).toUpperCase();
  const cls = ["pp-avatar", size !== "md" ? `pp-avatar--${size}` : "", brand ? "pp-avatar--brand" : ""].filter(Boolean).join(" ");
  return <span className={cls}>{initials}</span>;
}

/* ---------- Live dot ---------- */
function LiveDot({ label = "LIVE" }) {
  return <span className="pp-live"><span className="pp-live-dot" /> {label}</span>;
}

/* ---------- Field (label + control wrapper) ---------- */
function Field({ label, hint, error, children, action }) {
  return (
    <label className="pp-field">
      {(label || action) && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          {label && <span className="pp-field-label">{label}</span>}
          {action && <span style={{ fontSize: "12px" }}>{action}</span>}
        </div>
      )}
      {children}
      {hint && !error && <span className="pp-field-hint">{hint}</span>}
      {error && <span className="pp-field-hint" style={{ color: "var(--pp-crit)" }}>{error}</span>}
    </label>
  );
}

/* ---------- Segmented control ---------- */
function Segmented({ value, onChange, options }) {
  return (
    <div className="pp-seg" role="tablist">
      {options.map(opt => (
        <button
          key={opt.value}
          type="button"
          role="tab"
          aria-pressed={value === opt.value}
          onClick={() => onChange && onChange(opt.value)}
        >
          {opt.icon}
          {opt.label}
          {opt.count != null && <span style={{ opacity: 0.7, fontFamily: "var(--font-mono)" }}>{opt.count}</span>}
        </button>
      ))}
    </div>
  );
}

/* ---------- Severity radio group (large tactile picker) ---------- */
function SeverityRadio({ value, onChange }) {
  const opts = [
    { v: "low",      label: "Low",       sub: "Inconvenience", code: "low",  bars: 1 },
    { v: "moderate", label: "Moderate",  sub: "Slows workflow", code: "mod",  bars: 2 },
    { v: "critical", label: "Critical",  sub: "Stop / unsafe",  code: "crit", bars: 3 },
  ];
  return (
    <div className="pp-sev-group">
      {opts.map(o => (
        <label key={o.v} className="pp-sev-opt" data-active={value === o.v ? o.code : "none"}>
          <input type="radio" checked={value === o.v} onChange={() => onChange && onChange(o.v)} />
          <div style={{ display: "flex", gap: "3px" }}>
            {[1, 2, 3].map(i => (
              <span key={i} className="pp-sev-opt-bar" style={{ opacity: i <= o.bars ? 1 : 0.18 }} />
            ))}
          </div>
          <span className="pp-sev-opt-label">{o.label}</span>
          <span className="pp-sev-opt-sub">{o.sub}</span>
        </label>
      ))}
    </div>
  );
}

/* ---------- Tabs ---------- */
function Tabs({ value, onChange, tabs }) {
  return (
    <div className="pp-tabs" role="tablist">
      {tabs.map(t => (
        <button
          key={t.id}
          role="tab"
          className="pp-tab"
          aria-selected={value === t.id}
          onClick={() => onChange && onChange(t.id)}
        >
          {t.icon}{t.label}
          {t.count != null && <span className="pp-tab-count">{t.count}</span>}
        </button>
      ))}
    </div>
  );
}

/* ---------- Section header (with right slot) ---------- */
function SectionHead({ title, sub, right }) {
  return (
    <div className="pp-section-head">
      <div>
        <div className="pp-section-title">{title}</div>
        {sub && <div className="pp-section-sub">{sub}</div>}
      </div>
      {right && <div>{right}</div>}
    </div>
  );
}

/* ---------- Empty state ---------- */
function Empty({ icon, title, sub, action }) {
  return (
    <div className="pp-empty">
      {icon && <span className="pp-empty-icon">{icon}</span>}
      <div style={{ fontWeight: 600, color: "var(--text-dim)" }}>{title}</div>
      {sub && <div style={{ fontSize: "12px" }}>{sub}</div>}
      {action && <div style={{ marginTop: 8 }}>{action}</div>}
    </div>
  );
}

/* ---------- Format helpers ---------- */
function formatRelative(ts) {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// Expose primitives to global scope for sibling Babel scripts.
// Each file that uses primitives can read them off window.
Object.assign(window, {
  Button, Chip, SeverityChip, StatusChip, CategoryChip,
  StatusPipeline, Avatar, LiveDot, Field, Segmented,
  SeverityRadio, Tabs, SectionHead, Empty, formatRelative,
});

export {
  Button, Chip, SeverityChip, StatusChip, CategoryChip,
  StatusPipeline, Avatar, LiveDot, Field, Segmented,
  SeverityRadio, Tabs, SectionHead, Empty, formatRelative,
};
